import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  AVAILABLE_ROLES,
  fetchDirectusCatalog,
  mapDirectusRow,
  mapHeroRow,
  readStoredRole,
  storeRole,
} from '../lib/directus';
import { listCourses } from '../lib/learn';
import { setRemoteFlags } from '../lib/flags';
import {
  casketPackages,
  dialysisCentres,
  givingOptions,
  heroSlides as mockHeroSlides,
  learnHubItems as mockLearn,
  serviceHubItems as mockServices,
  volunteerHubItems as mockVolunteer,
} from '../data/mockData';

const OfferingsContext = createContext(null);

const HUB_KEYS = ['services', 'volunteer', 'learn', 'give', 'dialysis', 'casket'];

function roleCanSeeModule(item, role) {
  if (item.id === 'daily-attendance') return role === 'duty_officer';
  if (item.id === 'ulum-asatizah') return role === 'asatizah' || role === 'staff_learning';
  if (item.staffOnly) return role === 'duty_officer' || role === 'asatizah' || role === 'staff_learning';
  return true;
}

function mockCatalog(role) {
  const services = mockServices.filter((item) => roleCanSeeModule(item, role));
  return {
    source: 'mock',
    heroSlides: mockHeroSlides,
    collections: {
      services,
      volunteer: mockVolunteer,
      learn: mockLearn,
      give: givingOptions,
      dialysis: dialysisCentres,
      casket: casketPackages,
    },
  };
}

function fromDirectus(listingRows, moduleRows, role) {
  const grouped = Object.fromEntries(HUB_KEYS.map((key) => [key, []]));
  const heroSlides = [];
  for (const row of listingRows) {
    if (row.status && row.status !== 'published') continue;
    if (row.deleted_at) continue;
    if (row.hub === 'hero') {
      heroSlides.push(mapHeroRow(row));
      continue;
    }
    const hub = grouped[row.hub] ? row.hub : null;
    if (!hub) continue;
    grouped[hub].push(mapDirectusRow(row));
  }
  for (const row of moduleRows) {
    const mapped = mapDirectusRow(row);
    if (!roleCanSeeModule(mapped, role)) continue;
    grouped.services.push(mapped);
  }
  return {
    source: 'directus',
    heroSlides: heroSlides.length ? heroSlides : mockHeroSlides,
    collections: grouped,
  };
}

// Turn a Supabase `public.courses` row into a hub card shape. The mock/Directus
// entry (matched by id) supplies programme details and imagery; the Supabase
// row is the source of truth for date/fee/description so it takes precedence.
function courseRowToHubItem(row) {
  const scheduleDate = row.starts_at
    ? new Date(row.starts_at + 'T00:00:00').toLocaleDateString('en-SG', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
      })
    : null;
  const feeLabel = row.price_cents && row.price_cents > 0
    ? `SGD ${(row.price_cents / 100).toFixed(2)}`
    : 'Free';
  return {
    id: row.id,
    title: row.title,
    description: row.description || undefined,
    org: row.org || undefined,
    location: row.location || row.format || undefined,
    date: scheduleDate || row.duration_text || undefined,
    tag: row.category || undefined,
    badge: row.badge || undefined,
    isLMS: !!row.is_lms,
    priceCents: row.price_cents || 0,
    fee: feeLabel,
    // The card component reads these when present.
    image: row.image_url || undefined,
    heroImage: row.image_url || undefined,
  };
}

// Overlay Supabase course rows onto whatever the base catalogue (Directus or
// mock) already returned for the Learn hub. Matched by id: real fields from
// Supabase win; extras like carouselExtra/images stay from the base entry.
// Unmatched Supabase rows are appended so new seeded classes appear without
// needing a matching mock/Directus entry.
function overlayCourses(baseLearn, courseRows) {
  if (!Array.isArray(courseRows) || courseRows.length === 0) return baseLearn;
  const byId = new Map(courseRows.map((r) => [r.id, courseRowToHubItem(r)]));
  const seen = new Set();
  const merged = (baseLearn || []).map((item) => {
    const overlay = byId.get(item.id);
    if (!overlay) return item;
    seen.add(item.id);
    // Prune undefined overlay fields so we don't erase good base data.
    const clean = Object.fromEntries(
      Object.entries(overlay).filter(([, v]) => v !== undefined),
    );
    return { ...item, ...clean };
  });
  for (const [id, overlay] of byId.entries()) {
    if (!seen.has(id)) merged.push(overlay);
  }
  return merged;
}

export function OfferingsProvider({ children }) {
  const [role, setRoleState] = useState(() => (
    typeof window === 'undefined' ? 'member' : readStoredRole()
  ));
  const [payload, setPayload] = useState(() => ({ loading: true, ...mockCatalog('member') }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Fetch the base catalogue (Directus with mock fallback) and the real
      // Supabase courses in parallel — the Supabase overlay refreshes real
      // dates/fees on Learn cards even when Directus is down.
      const [live, courseRows] = await Promise.all([
        fetchDirectusCatalog(role),
        listCourses(),
      ]);
      if (cancelled) return;
      const base = live
        ? fromDirectus(live.listingRows, live.moduleRows, role)
        : mockCatalog(role);
      if (live) setRemoteFlags(live.flagRows);
      const collections = {
        ...base.collections,
        learn: overlayCourses(base.collections.learn, courseRows),
      };
      setPayload({ loading: false, source: base.source, heroSlides: base.heroSlides, collections });
    })();
    return () => {
      cancelled = true;
    };
  }, [role]);

  const setRole = (next) => {
    storeRole(next);
    setRoleState(next);
  };

  const value = useMemo(() => {
    const collections = payload.collections;
    return {
      loading: payload.loading,
      source: payload.source,
      role,
      setRole,
      roles: AVAILABLE_ROLES,
      collections,
      heroSlides: payload.heroSlides,
      serviceHubItems: collections.services,
      volunteerHubItems: collections.volunteer,
      learnHubItems: collections.learn,
      givingOptions: collections.give,
      dialysisCentres: collections.dialysis,
      casketPackages: collections.casket,
      detailCollections: collections,
    };
  }, [payload, role]);

  return (
    <OfferingsContext.Provider value={value}>
      {children}
    </OfferingsContext.Provider>
  );
}

export function useOfferings() {
  const value = useContext(OfferingsContext);
  if (!value) throw new Error('useOfferings must be used inside OfferingsProvider');
  return value;
}
